import { useState, useEffect, useCallback } from 'react';
import { Container, Typography, TextField, Button, Grid } from '@mui/material';
import firebase from 'firebase/compat/app';
import Snackbar, { SnackbarType } from '../../shared/Snackbar';

/**
 * Address data structure for admin management
 */
interface AdminAddress {
  id: number;
  address: string;
  amount: number | string;
  lastPaid: string;
  paymentStatus: string;
}

/**
 * AdminBoard Component
 * Property management dashboard for administrators
 *
 * Features:
 * - Add/remove rental properties
 * - Edit property details (address, rent amount, payment status)
 * - Sync property data with Firebase
 * - Visual selection/management interface
 */
export function AdminBoard() {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState<SnackbarType>('success');
  const [isVisible, setIsVisible] = useState(false);
  const [addresses, setAddresses] = useState<AdminAddress[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const showNotification = (message: string, type: SnackbarType = 'success') => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setSnackbarOpen(true);
  };

  const retrieveAddressData = useCallback(() => {
    const addressInfoRef = firebase.database().ref('addresses/');
    addressInfoRef.once('value').then((snapshot) => {
      const addressData = snapshot.val();
      if (!addressData) {
        setAddresses([]);
        return;
      }
      const flattenedData = Object.keys(addressData).map((fullAddress, index) => {
        const data = addressData[fullAddress];
        return {
          id: index + 1,
          address: fullAddress,
          amount: Number(data.rentAmount) || 0,
          lastPaid: data.paymentDate || '',
          paymentStatus: data.rentPaid || ''
        };
      });
      setAddresses(flattenedData);
    }).catch(error => {
      showNotification('Error loading properties. Please try again.', 'error');
    });
  }, []);

  useEffect(() => {
    retrieveAddressData();
    setTimeout(() => {
      setIsVisible(true);
    }, 100);
  }, [retrieveAddressData]);

  const addBlankRow = () => {
    const newId = addresses.length ? addresses[addresses.length - 1].id + 1 : 1;
    const newAddress = { id: newId, address: '', amount: 0, lastPaid: '', paymentStatus: '' };
    setAddresses([...addresses, newAddress]);
  };

  const removeSelectedRows = () => {
    const updatedAddresses = addresses.filter((address) => !selectedRows.includes(address.id));
    setAddresses(updatedAddresses);
    setSelectedRows([]);
  };

  const updateFirebaseData = () => {
    const addressesRef = firebase.database().ref('addresses');
    addressesRef.once('value').then((snapshot) => {
      return addressesRef.remove();
    }).then(() => {
      const updates: Record<string, {
        rentAmount: number | string;
        paymentDate: string;
        rentPaid: string;
      }> = {};
      addresses.forEach((address) => {
        if (!address.address || address.address.trim() === '') {
          return;
        }
        const sanitizedAddress = address.address.replace(/[.#$[\]]/g, '_');
        updates[sanitizedAddress] = {
          rentAmount: address.amount,
          paymentDate: address.lastPaid,
          rentPaid: address.paymentStatus,
        };
      });
      if (Object.keys(updates).length === 0) {
        showNotification('No valid addresses to update', 'warning');
        return Promise.resolve();
      }
      return addressesRef.update(updates);
    }).then(() => {
      showNotification('Properties updated successfully');
    }).catch((error) => {
      showNotification('Error updating properties. See console for details.', 'error');
    });
  };

  const handleInputChange = (id: number, field: keyof AdminAddress, value: string | number) => {
    const updatedAddresses = addresses.map((address) =>
      address.id === id ? { ...address, [field]: value } : address
    );
    setAddresses(updatedAddresses);
  };

  return (
    <div className="admin-wrapper">
      <Container className={`admin-container ${isVisible ? 'admin-visible' : ''}`}>
        <div className="admin-header">
          <Typography variant="h4" className="admin-title">
            Rental Properties
          </Typography>
          <p className="admin-subtitle">Manage your rental properties and tenant information</p>
        </div>

        <div className='admin-buttons'>
          <Button
            variant="contained"
            className="admin-button admin-button-add"
            onClick={addBlankRow}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
              <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Add Property
          </Button>
          <Button
            variant="contained"
            className="admin-button admin-button-remove"
            onClick={removeSelectedRows}
            disabled={selectedRows.length === 0}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
              <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Remove Selected
          </Button>
        </div>

        <div className="admin-properties-grid">
          {addresses.map((address) => (
            <div
              key={address.id}
              className={`admin-property-card ${selectedRows.includes(address.id) ? 'admin-property-selected' : ''}`}
              onClick={() => {
                if (selectedRows.includes(address.id)) {
                  setSelectedRows(selectedRows.filter(id => id !== address.id));
                } else {
                  setSelectedRows([...selectedRows, address.id]);
                }
              }}
            >
              <div className="admin-property-header">
                <div className="admin-property-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="admin-property-selector">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(address.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (selectedRows.includes(address.id)) {
                        setSelectedRows(selectedRows.filter(id => id !== address.id));
                      } else {
                        setSelectedRows([...selectedRows, address.id]);
                      }
                    }}
                    className="admin-checkbox"
                  />
                </div>
              </div>

              <Grid container spacing={2} onClick={(e) => e.stopPropagation()}>
                <Grid item xs={12}>
                  <TextField
                    label="Address"
                    value={address.address}
                    onChange={(e) => handleInputChange(address.id, 'address', e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Rent Amount"
                    type="number"
                    value={address.amount}
                    onChange={(e) => handleInputChange(address.id, 'amount', e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: <span style={{ marginRight: '4px' }}>$</span>
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Last Paid Date"
                    value={address.lastPaid}
                    onChange={(e) => handleInputChange(address.id, 'lastPaid', e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="MM/DD/YYYY"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Payment Status"
                    value={address.paymentStatus}
                    onChange={(e) => handleInputChange(address.id, 'paymentStatus', e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    select
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="">Select status</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="overdue">Overdue</option>
                  </TextField>
                </Grid>
              </Grid>
            </div>
          ))}

          {addresses.length === 0 && (
            <div className="admin-empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h3>No Properties Yet</h3>
              <p>Click "Add Property" to get started</p>
            </div>
          )}
        </div>

        <div className="admin-footer">
          <Button
            variant="contained"
            className='admin-submit-button'
            onClick={updateFirebaseData}
            disabled={addresses.length === 0}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
              <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Save All Changes
          </Button>
        </div>

        <Snackbar
          open={snackbarOpen}
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setSnackbarOpen(false)}
          autoHideDuration={1000}
        />
      </Container>
    </div>
  );
}

export default AdminBoard;